import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  User,
  Briefcase,
  Link2,
  MessageSquare,
  LogOut,
  Loader2,
  CheckCircle,
  Clock,
  Calendar,
  DollarSign,
  MapPin,
  Building,
  Download,
  ExternalLink,
} from 'lucide-react';
import api from '../lib/api';
import { removeToken } from '../lib/auth';
import { getStatusColor, formatDateTime } from '../lib/utils';

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);

  useEffect(() => {
    const loadApplication = async () => {
      try {
        const response = await api.get(`/api/applications/${id}`);
        setApplication(response.data);
      } catch (error) {
        toast.error('Failed to load application');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadApplication();
  }, [id]);

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/40 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (!application) return null;

  const canOnboard = application.status === 'Hired';

  return (
    <div className="min-h-screen bg-white/40">
      {/* Header */}
      <header className="bg-white border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Application Details</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-neutral-600 hover:text-navy-900 mb-6 transition-colors"
          data-testid="back-to-dashboard-link"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8 mb-6" data-testid="application-detail-header">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-4xl font-bold text-navy-900 mb-2">
                {application.role?.title || 'Application'}
              </h1>
              <p className="text-lg text-neutral-600 mb-2">
                {application.role?.department} • {application.role?.location} • {application.role?.employment_type}
              </p>
              <p className="text-sm text-neutral-500">
                Application submitted on {formatDateTime(application.created_at)}
              </p>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(
                application.status
              )}`}
              data-testid="status-badge"
            >
              {application.status}
            </span>
          </div>

          {/* Offer Letter Download */}
          {application.status === 'Hired' && application.offer_letter && (
            <div className="mt-6 pt-6 border-t border-white/40">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-900 mb-1">
                      🎉 Congratulations! You&apos;re Hired!
                    </h3>
                    <p className="text-sm text-emerald-700">
                      Your offer letter is ready for download
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.get(`/api/applications/${application._id}/offer-letter`);
                        const blob = new Blob([response.data.content], { type: 'text/plain' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Offer_Letter_${application.personal.full_name.replace(/ /g, '_')}.txt`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success('Offer letter downloaded!');
                      } catch (error) {
                        toast.error('Failed to download offer letter');
                      }
                    }}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all shadow-soft hover:shadow-soft font-medium flex items-center"
                    data-testid="download-offer-letter-button"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Offer Letter
                  </button>
                </div>
              </div>
            </div>
          )}

          {canOnboard && (
            <div className="mt-6 pt-6 border-t border-white/40">
              <Link
                to={`/onboarding/${application._id}`}
                className="inline-flex items-center px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium"
                data-testid="start-onboarding-button"
              >
                Start Onboarding Process
              </Link>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="personal-info-section">
              <div className="flex items-center gap-3 mb-4">
                <User className="w-6 h-6 text-teal-600" />
                <h2 className="text-2xl font-semibold text-navy-800">
                  Personal Information
                </h2>
              </div>
              <div className="space-y-3 text-neutral-700">
                <p>
                  <span className="font-medium text-navy-700">Name:</span>{' '}
                  {application.personal.full_name}
                </p>
                <p>
                  <span className="font-medium text-navy-700">Email:</span>{' '}
                  {application.personal.email}
                </p>
                <p>
                  <span className="font-medium text-navy-700">Phone:</span>{' '}
                  {application.personal.phone}
                </p>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="professional-info-section">
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="w-6 h-6 text-teal-600" />
                <h2 className="text-2xl font-semibold text-navy-800">
                  Professional Background
                </h2>
              </div>
              <div className="space-y-3 text-neutral-700">
                <div>
                  <p className="font-medium text-navy-700 mb-2">Past Experience:</p>
                  <p className="whitespace-pre-wrap bg-white/40 p-4 rounded-xl">
                    {application.professional.past_experience}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {application.professional.current_ctc && (
                    <div>
                      <p className="font-medium text-navy-700">Current CTC:</p>
                      <p className="text-lg">₹ {application.professional.current_ctc.toLocaleString('en-IN')}</p>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-navy-700">Expected CTC:</p>
                    <p className="text-lg">₹ {application.professional.expected_ctc.toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-navy-700">Total Experience:</p>
                    <p>{application.professional.experience_years} years</p>
                  </div>
                  {application.professional.notice_period && (
                    <div>
                      <p className="font-medium text-navy-700">Notice Period:</p>
                      <p>{application.professional.notice_period}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-teal-600" />
                <h2 className="text-2xl font-semibold text-navy-800">Documents</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-navy-700 mb-2">Resume:</p>
                  {application.docs.resume_type === 'upload' ? (
                    <a
                      href={`/api/uploads/${application.docs.resume_file_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-teal-600 hover:text-teal-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Resume
                    </a>
                  ) : (
                    <a
                      href={application.docs.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-teal-600 hover:text-teal-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Resume
                    </a>
                  )}
                </div>
                {application.docs.portfolio_file_id || application.docs.portfolio_url ? (
                  <div>
                    <p className="font-medium text-navy-700 mb-2">Portfolio:</p>
                    {application.docs.portfolio_type === 'upload' ? (
                      <a
                        href={`/api/uploads/${application.docs.portfolio_file_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-teal-600 hover:text-teal-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Portfolio
                      </a>
                    ) : (
                      <a
                        href={application.docs.portfolio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-teal-600 hover:text-teal-700"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Portfolio
                      </a>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Eligibility Acknowledgment */}
            {application.eligibility_acknowledged && (
              <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  <h2 className="text-2xl font-semibold text-navy-800">
                    Eligibility Acknowledged
                  </h2>
                </div>
                <p className="text-sm text-neutral-600 mb-3">
                  Acknowledged on {formatDateTime(application.eligibility_acknowledged.acknowledged_at)}
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {Object.entries(application.eligibility_acknowledged).map(([key, value]) => {
                    if (key === 'acknowledged_at') return null;
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span className="text-neutral-700">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Fields */}
            {application.custom_fields && Object.keys(application.custom_fields).length > 0 && (
              <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6">
                <h2 className="text-2xl font-semibold text-navy-800 mb-4">
                  Additional Information
                </h2>
                <div className="space-y-3 text-neutral-700">
                  {Object.entries(application.custom_fields).map(([key, value]) => (
                    <p key={key}>
                      <span className="font-medium text-navy-700">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                      </span>{' '}
                      {value}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Interview Details */}
            {application.interview && (
              <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-purple-600" />
                  <h2 className="text-2xl font-semibold text-purple-900">
                    Interview Scheduled
                  </h2>
                </div>
                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-sm text-purple-700 font-medium mb-1">Date & Time</p>
                    <p className="text-lg font-semibold text-purple-900">
                      {formatDateTime(application.interview.scheduled_at)}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-sm text-purple-700 font-medium mb-1">Location / Meeting Link</p>
                    {application.interview.location.startsWith('http') ? (
                      <a
                        href={application.interview.location}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg font-semibold text-teal-600 hover:text-teal-700 hover:underline flex items-center"
                      >
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Join Interview Meeting
                      </a>
                    ) : (
                      <p className="text-lg font-semibold text-purple-900">
                        {application.interview.location}
                      </p>
                    )}
                  </div>
                  {application.interview.notes && (
                    <div className="bg-white rounded-xl p-4">
                      <p className="text-sm text-purple-700 font-medium mb-1">Notes</p>
                      <p className="text-neutral-700">{application.interview.notes}</p>
                    </div>
                  )}
                  {application.interview.rescheduled_from && (
                    <p className="text-sm text-purple-600 italic">
                      (Rescheduled from {formatDateTime(application.interview.rescheduled_from)})
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Timeline */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6 sticky top-6" data-testid="timeline-section">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-teal-600" />
                <h2 className="text-2xl font-semibold text-navy-800">Timeline</h2>
              </div>

              <div className="space-y-6">
                {application.timeline.map((item, index) => {
                  const isLast = index === application.timeline.length - 1;
                  const isCurrent = index === application.timeline.length - 1;

                  return (
                    <div
                      key={index}
                      className={`relative ${!isLast ? 'pb-6' : ''}`}
                      data-testid="timeline-item"
                    >
                      {!isLast && (
                        <div className="absolute left-[15px] top-8 w-0.5 h-full bg-neutral-200" />
                      )}
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                            isCurrent
                              ? 'border-teal-500 bg-teal-500'
                              : 'border-emerald-500 bg-emerald-500'
                          }`}
                        >
                          {isCurrent ? (
                            <Clock className="w-4 h-4 text-white" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy-800 mb-1">
                            {item.status}
                          </p>
                          {item.note && (
                            <p className="text-sm text-neutral-600 mb-1">
                              {item.note}
                            </p>
                          )}
                          {item.by_admin && (
                            <p className="text-xs text-neutral-500">
                              by {item.by_admin}
                            </p>
                          )}
                          <p className="text-xs text-neutral-500">
                            {formatDateTime(item.at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
